import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

const MAX_TOKENS = 400;

/**
 * Derives the correct API host, matching the pattern used by other editor AI
 * features in editor-plugin-ai/src/ui/prebuilt/utils.ts.
 *
 * On localhost (non-port-8081) and Atlaskit storybook hosts, routes to
 * pug.jira-dev.com so API calls work without a local backend.
 * In all other environments (staging, prod) uses window.location.origin.
 */
function deriveHost(): string {
	const {
		origin = '',
		port = '',
		hostname = '',
	} = typeof window !== 'undefined' && window.location ? window.location : {};

	if (
		(hostname === 'localhost' && port !== '8081') ||
		[
			'atlaskit-website-staging.stg-east.frontend.public.atl-paas.net',
			'statlas.prod.atl-paas.net',
			'atlaskit.atlassian.com',
		].some((h) => hostname === h)
	) {
		return 'https://pug.jira-dev.com';
	}

	return origin;
}

// Convo-AI path (current source of truth, served by conversational-ai-platform).
const CONVO_AI_PATH = '/gateway/api/assist/api/ai/v2/ai-feature/heading-autocomplete';

// cc-smarts path (new home, served by cc-smarts). Reached via the
// cc-smarts-ai-feature stargate route (stargate PR-8755), which strips
// /gateway/api and prepends /api before forwarding to cc-smarts.
const CC_SMARTS_PATH = '/gateway/api/ai/v2/ai-feature/heading-autocomplete';

/**
 * Resolves the heading-autocomplete endpoint URL based on the SPRT routing experiment.
 *
 * Routing: the editor experiment `cc_smarts_heading_autocomplete_migration` (param
 * `useCcSmarts`) decides between cc-smarts (new home, test arm) and conversational-ai-platform
 * (current source of truth, control arm). Wire shape is byte-for-byte identical between
 * the two backends; only the URL path differs. Mirrors `cc_smarts_comment_summary_followups_migration`
 * (AFM PR-388153).
 *
 * On any read failure (experiment lookup throws, Statsig unavailable, etc.) callers fail
 * closed to the convo-ai control arm — that's the safer arm because it's the production
 * source of truth today.
 */
function getApiEndpoint(): string {
	// Fail-closed: useCcSmarts stays at its initialised value of `false` if the experiment read throws,
	// landing on the convo-ai control arm — the production source of truth today. Do not move the
	// initialiser out of this declaration or remove the empty catch; both are load-bearing for the
	// fail-closed property (per the convo-ai → cc-smarts migration skill guardrails).
	let useCcSmarts = false;
	try {
		useCcSmarts = isExperimentEnabled('cc_smarts_heading_autocomplete_migration');
	} catch {
		// Intentionally empty — see the fail-closed comment on the `let` above.
	}
	const path = useCcSmarts ? CC_SMARTS_PATH : CONVO_AI_PATH;
	return `${deriveHost()}${path}`;
}

/**
 * Calls the heading autocomplete AI endpoint and returns the generated paragraph text.
 *
 * Uses the same host derivation as other editor AI features (deriveHost()), which
 * correctly routes to pug.jira-dev.com on localhost and to the gateway on production.
 *
 * Routing: see `getApiEndpoint` Javadoc — the SPRT experiment
 * `cc_smarts_heading_autocomplete_migration` (param `useCcSmarts`) determines whether
 * the request goes to cc-smarts (new home) or to conversational-ai-platform (current
 * source of truth). Wire shape is byte-for-byte identical between the two backends;
 * only the URL changes.
 *
 * @param pageContentMarkdown - The full page content serialised to Markdown, with a
 *   `<!-- GENERATE_HERE -->` comment injected after the heading to complete.
 * @param sectionType - Optional section type hint passed through to the backend.
 * @param signal - AbortSignal so we can cancel in-flight requests when the user
 *   continues typing or navigates away.
 */
export async function callHeadingAutocompleteApi(
	pageContentMarkdown: string,
	sectionType: string | undefined,
	signal: AbortSignal,
): Promise<string> {
	const response = await fetch(getApiEndpoint(), {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'X-Experience-Id': 'editor',
			'X-Product': 'confluence',
		},
		credentials: 'include',
		signal,
		body: JSON.stringify({
			ai_feature_input: {
				page_content_markdown: pageContentMarkdown,
				max_tokens: MAX_TOKENS,
				section_type: sectionType,
			},
		}),
	});

	if (!response.ok) {
		throw new Error(`Heading autocomplete API error: ${response.status} ${response.statusText}`);
	}

	const data = await response.json();
	const generatedParagraph: string | undefined = data?.ai_feature_output?.generated_paragraph;

	if (!generatedParagraph) {
		throw new Error('Heading autocomplete API returned an empty or malformed response');
	}

	return generatedParagraph;
}
